import net, { Socket } from 'net'
import { IO, NumberFromModbus, InterpretModbusDataType, getNameForBufferOperations, Endianity, IOValuePair } from './IO'
import { ModbusTCPClient, UserRequestError, client } from 'jsmodbus'
import { Group } from './group'
import { z } from 'zod'

/**
 * Options for the Modbus client.
 */
type ModbusClientOptions = {
    /**
     * Interval of polling new data in miliseconds.
     * Defaults to 100.
     */
    pollInterval: number
    /**
     * Endianity that will be used for reading and writing to the buffer.
     * Defaults to 'BE'.
     */
    endianity: Endianity
}

type Callback<T> = (value: T) => void

/**
 * The client for communication with the Modbus server.
 */
class ModbusClient {
    protected _client?: ModbusTCPClient
    private _socket?: Socket
    private readonly pollInterval: number
    private readonly endianity: Endianity
    private listeners: Map<IO<InterpretModbusDataType>, Callback<number | boolean | bigint>[]>
    private intervalId?: NodeJS.Timeout
    private previousValues: Map<IO<InterpretModbusDataType>, number | boolean | bigint>
    private inited: boolean
    private previousConnectionOptions?: Partial<ModbusClientConnectionOptions>

    constructor(options?: Partial<ModbusClientOptions>) {
        // this._client = client
        this.endianity = options?.endianity || 'BE'
        this.pollInterval = options?.pollInterval || 100
        this.listeners = new Map()
        this.previousValues = new Map()
        this.inited = false
    }

    /**
     * Try to handle Modbus error. If the error cannot be handled, it is rethrown.
     * @param error the error to handle
     */
    /* eslint-disable @typescript-eslint/no-explicit-any */
    private async handleModbusError(error: UserRequestError<any>) {
        console.log(error)
        if (error.err === 'Offline') {
            if (this._client?.connectionState === 'offline') {
                await this._init()
            }
        } else {
            throw error
        }
    }

    /** Asserts that modbus client is initialized. Throws otherwise. */
    private assertClientInited(): asserts this is this & { _client: ModbusTCPClient } {
        if (!this._client) {
            throw new Error('Cannot call this when the client is not initialized.')
        }
    }

    /**
     * Create the actual connection to the Modbus server.
     * When the client is inited, it must also be destroyed/disposed of either manually
     * or with `await using`.
     * @param options the connection options
     *
     * @example
     * async function writeBytes() {
     *  const client = new ModbusClient()
     *  await client.init()
     *
     *  // ... work with the client ...
     *
     *  await client.destroy()
     * }
     */
    public async init(options?: Partial<ModbusClientConnectionOptions>) {
        this.previousConnectionOptions = options

        if (this.inited) {
            throw new Error('modbus client cannot be inited multiple times')
        }

        await this._init()
    }

    private _init() {
        return new Promise<void>((res) => {
            if (this.inited) {
                res()
                return
            }
            this.inited = true

            this._socket = new net.Socket()
            this._client = new client.TCP(this._socket, this.previousConnectionOptions?.socketUnitId)

            this._socket.connect(
                this.previousConnectionOptions?.port ?? 502,
                this.previousConnectionOptions?.address ?? '127.0.0.1'
            )

            this._socket.on('connect', () => {
                res()
            })

            this._socket.on('error', () => {
                this.inited = false
            })
            this._socket.on('timeout', () => {
                this.inited = false
            })
            this._socket.on('end', () => {
                this.inited = false
            })
        })
    }

    /**
     * Close the connection to socket and clear any remaining timeouts.
     * If there are any registered callbacks left or the client was not initialized,
     * will reject the promise. Alternatively, instead of calling destroy(),
     * you can use the async dispose resource management. When using `await using`,
     * don't destroy the client manually.
     * @example
     * async function writeBytes() {
     *  await using client = new ModbusClient()
     *  await client.init()
     *
     *  // ... work with the client ...
     *
     *  // client is automatically disposed/destroyed here,
     *  // when it goes out of scope
     * }
     */
    public async destroy() {
        return this._destroy()
    }

    /**
     * Close the underlying connection to socket, clear uncleared timeouts and optionally throw when
     * trying to close an unitialised client.
     * @param failWhenUnitialized when the client was not uninitialized, should fail or not
     */
    private async _destroy() {
        return new Promise<void>((res, rej) => {
            if (!this.inited) {
                return res()
            }

            this._socket?.end(() => {
                if (this.intervalId) {
                    clearTimeout(this.intervalId)
                    this.intervalId = undefined
                    return rej(new Error('Destroyed client with still active listeners.'))
                }

                res()
            })
        })
    }

    /**
     * Interpret the value in buffer as the provided type.
     * @param buffer raw data
     * @param type the type of the raw data
     * @param from starting byte (0-indexed) of the data in the buffer
     * @param bitOffset bit offset of the data in the buffer, relevant only for type boolean
     * @returns
     */
    private readBufferAsType<T extends InterpretModbusDataType>(
        buffer: Buffer,
        type: T,
        from = 0,
        bitOffset = 0
    ): NumberFromModbus<T> {
        if (type === 'Bool') {
            // tslint:disable-next-line:no-bitwise
            return !!(buffer[from] & (1 << bitOffset)) as R
        }

        type R = NumberFromModbus<T>
        const methodName = `read${getNameForBufferOperations(type, this.endianity)}` as const
        if (
            methodName === 'readBigInt64BE' ||
            methodName === 'readBigUInt64BE' ||
            methodName === 'readBigInt64LE' ||
            methodName === 'readBigUInt64LE'
        ) {
            return buffer[methodName](from) as R
        }

        return buffer[methodName](from) as R
    }

    /**
     * Performs a read request to the Modbus server.
     * @param io the io to read
     * @returns value of the io
     */
    public async read<T extends InterpretModbusDataType>(io: IO<T>): Promise<NumberFromModbus<T>> {
        this.assertClientInited()
        const { startingElement, quantity, operateOn } = io.group

        let rawData
        switch (operateOn) {
            case 'COIL':
                rawData = await this._client
                    .readCoils(startingElement, quantity)
                    .catch(this.handleModbusError.bind(this))
                break
            case 'DISCRETE_INPUT':
                rawData = await this._client
                    .readDiscreteInputs(startingElement, quantity)
                    .catch(this.handleModbusError.bind(this))
                break
            case 'HOLDING_REGISTER':
                rawData = await this._client
                    .readHoldingRegisters(startingElement, quantity)
                    .catch(this.handleModbusError.bind(this))
                break
            default:
                throw new Error('Unknown type ' + operateOn + ' for reading.')
        }

        if (!rawData) {
            throw new Error('Unable to read data from Modbus.')
        }

        const res = rawData.response.body.valuesAsBuffer
        return this.readBufferAsType(res, io.type, io.dataOffset, io.bitOffset)
    }

    /**
     * Write ios from the same group as a simultaneous block of data in one call.
     * Only ios from one group may appear.
     * @param data list of the tuples <io, new-value> to write
     */
    public async writeGroupAsWhole(data: IOValuePair[]) {
        this.assertClientInited()
        let group: Group | null = null

        for (const [io] of data) {
            if (group !== null && group !== io.group) {
                throw new Error(
                    `IO ${io.name} does not belong to the group ${group}. Only one group may appear in the writeGroupAsWhole method.`
                )
            }
            group = io.group
        }

        if (group?.operateOn === 'COIL') {
            const bits: boolean[] = new Array(group.quantity)
            for (const [io, value] of data) {
                bits[io.dataOffset * 8 + io.bitOffset] = !!value
            }
            await this._client.writeMultipleCoils(group.startingElement, bits).catch(this.handleModbusError.bind(this))
        }

        if (group?.operateOn === 'HOLDING_REGISTER') {
            const bytes = Buffer.alloc(group.quantity * 2)

            for (const [io, value] of data) {
                const repr = this.getRepresentationOfValue(io, value)

                if (!(repr instanceof Buffer)) {
                    throw new Error('holding registers support only writing buffers')
                }

                if (io.writeOffsetGap - io.dataOffset !== repr.byteLength) {
                    throw new Error(
                        'invalid representation size ' + repr.byteLength + ' : ' + (io.writeOffsetGap - io.dataOffset)
                    )
                }

                bytes.set(repr, io.dataOffset)
            }

            await this._client
                .writeMultipleRegisters(group.startingElement, bytes)
                .catch(this.handleModbusError.bind(this))
        }
    }

    /**
     * Performs a request to the modbus server to write the value to the provided io.
     * @param io the io to write to
     * @param value the value to write
     */
    public async write<T extends InterpretModbusDataType>(io: IO<T>, value: NumberFromModbus<T>) {
        this.assertClientInited()
        const { startingElement } = io.group
        const adr = startingElement + Math.floor(io.dataOffset / 2)

        const repr = this.getRepresentationOfValue(io, value)
        if (repr === true || repr === false) {
            await this._client
                .writeSingleCoil(startingElement + io.dataOffset * 8 + io.bitOffset, repr)
                .catch(this.handleModbusError.bind(this))
        } else if (repr instanceof Buffer) {
            const maxLen = io.writeOffsetGap - io.dataOffset
            if (repr.length > maxLen) {
                throw new Error(`Cannot write ${repr.length} bytes to ${io.name}, max size is ${maxLen}.`)
            }
            await this._client.writeMultipleRegisters(adr, repr).catch(this.handleModbusError.bind(this))
        } else {
            throw new Error('Only true/false or buffers can be written.')
        }
    }

    /**
     * Transforms a numeric value to a buffer or boolean that can then be written to Modbus server.
     * The value in buffer is represented according to the type of forIO and endianity of Modbus.
     * @param forIO io point
     * @param value numeric value
     * @returns buffer of the value according to the type of provided forIO
     */
    private getRepresentationOfValue<T extends InterpretModbusDataType>(
        forIO: IO<T>,
        value: NumberFromModbus<T>
    ): boolean | Buffer {
        switch (forIO.group.operateOn) {
            case 'COIL':
                switch (forIO.type) {
                    case 'Bool':
                        return !!value

                    default:
                        throw new Error('unreachable COIL')
                }

            case 'HOLDING_REGISTER': {
                const size = Number.parseInt(forIO.type.substring(forIO.type.length - 2)) / 8
                const buffer = Buffer.from(new Array(size).fill(0))

                if (forIO.type === 'Bool') throw new Error('Cannot write boolean to holding register')

                const methodName = `write${getNameForBufferOperations(forIO.type, this.endianity)}` as const

                if (
                    methodName === 'writeBigInt64BE' ||
                    methodName === 'writeBigUInt64BE' ||
                    methodName === 'writeBigInt64LE' ||
                    methodName === 'writeBigUInt64LE'
                ) {
                    buffer[methodName](z.bigint().parse(value))
                } else {
                    buffer[methodName](z.number().parse(value))
                }

                return buffer
            }

            case 'DISCRETE_INPUT':
                throw new Error('Discrete input cannot be written.')

            case 'INPUT_REGISTER':
                throw new Error('Input register cannot be written.')

            default:
                throw new Error('unreachable WRITE')
        }
    }

    /**
     * Fetches the new values for observed ios and if some value changes, will call
     * the propper callback. There will be as many
     * requests to the Modbus server as there is different groups among the watched io points.
     */
    private async fetchNewValues() {
        this.assertClientInited()
        const groups: Set<Group> = new Set()

        for (const _io of this.listeners.keys()) {
            groups.add(_io.group)
        }

        const fetchedGroups = await Promise.all(
            Array.from(groups).map(
                (g) =>
                    new Promise<[Group, Buffer]>((res) => {
                        switch (g.operateOn) {
                            case 'HOLDING_REGISTER':
                                this._client
                                    .readHoldingRegisters(g.startingElement, g.quantity)
                                    .then(({ response }) => {
                                        res([g, response.body.valuesAsBuffer])
                                    })
                                    .catch(this.handleModbusError.bind(this))
                                break

                            case 'DISCRETE_INPUT':
                                this._client
                                    .readDiscreteInputs(g.startingElement, g.quantity)
                                    .then(({ response }) => {
                                        res([g, response.body.valuesAsBuffer])
                                    })
                                    .catch(this.handleModbusError.bind(this))
                                break

                            case 'COIL':
                                this._client
                                    .readCoils(g.startingElement, g.quantity)
                                    .then(({ response }) => {
                                        res([g, response.body.valuesAsBuffer])
                                    })
                                    .catch(this.handleModbusError.bind(this))
                                break

                            case 'INPUT_REGISTER':
                                this._client
                                    .readInputRegisters(g.startingElement, g.quantity)
                                    .then(({ response }) => {
                                        res([g, response.body.valuesAsBuffer])
                                    })
                                    .catch(this.handleModbusError.bind(this))
                                break
                        }
                    })
            )
        )

        const data = new Map(fetchedGroups)
        for (const _io of this.listeners.keys()) {
            const prev = this.previousValues.get(_io)
            const value = this.readBufferAsType(data.get(_io.group)!, _io.type, _io.dataOffset, _io.bitOffset)

            this.previousValues.set(_io, value)
            if (prev === undefined || prev !== value) {
                for (const cb of this.listeners.get(_io) ?? []) {
                    cb(value)
                }
            }
        }
    }

    /**
     * Register a callback for io. When io value changes, the callback is called with this new value.
     * If a value of given io is already known when registering a callback
     * (when the io is being fetched because it already has some callbacks registered),
     * the callback will be called right away with this value.
     * @param io the io to observe
     * @param callback the method to call with the new value of io
     */
    public registerCallback<T extends InterpretModbusDataType>(io: IO<T>, callback: Callback<NumberFromModbus<T>>) {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        this.listeners.set(io, [...(this.listeners.get(io) ?? []), callback as any])

        const currentValue = this.previousValues.get(io)

        if (currentValue !== undefined) {
            callback(currentValue as NumberFromModbus<T>)
        }

        if (this.intervalId === undefined) {
            const handleTimeout = () => {
                this.intervalId = setTimeout(async () => {
                    await this.fetchNewValues()
                    if (this.intervalId !== undefined) {
                        handleTimeout()
                    }
                }, this.pollInterval)
            }

            handleTimeout()
        }
    }

    /**
     * Remove the callback for given io.
     * Before disposing of the client, all callbacks should be unregistered.
     * @param io the io to unregister from
     * @param callback the callback to unregister for given io
     */
    public unregisterCallback<T extends InterpretModbusDataType>(io: IO<T>, callback: Callback<NumberFromModbus<T>>) {
        const next = (this.listeners.get(io) ?? []).filter((cb) => cb !== callback)
        this.listeners.set(io, next)

        if (next.length === 0) {
            this.listeners.delete(io)
            this.previousValues.delete(io)
        }

        if (Array.from(this.listeners.keys()).length === 0) {
            clearTimeout(this.intervalId)
            this.intervalId = undefined
        }
    }

    async [Symbol.asyncDispose]() {
        return this._destroy()
    }
}

/**
 * Options to connect to the Modbus server.
 */
type ModbusClientConnectionOptions = {
    /** default value is address 127.0.0.1 */
    address: string
    /** default is port 502 */
    port: number
    /** id of the client, not used if not provided */
    socketUnitId: number
}

export { ModbusClient, ModbusClientOptions }
